import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

// Possible animation directions used by `.reveal[data-anim]` rules in styles.css.
type RevealAnim = 'fade' | 'up' | 'left' | 'right' | 'scale' | 'blur';

// Tag names we want to support — kept narrow so the typing stays simple.
type RevealTag = 'div' | 'section' | 'article' | 'aside' | 'h1' | 'h2' | 'h3' | 'p' | 'blockquote' | 'span' | 'figure';

interface RevealProps {
  as?: RevealTag;
  className?: string;
  anim?: RevealAnim;
  /** Stagger entrance — useful when several reveals fire at once. */
  delay?: number;
  style?: CSSProperties;
  children?: ReactNode;
  // Allow extra attrs like id, aria-*, etc. — keep escape hatch loose.
  [key: string]: unknown;
}

/**
 * Reveal-on-scroll wrapper. Adds `.in` once the element enters the viewport.
 * Triggers once, then disconnects the IntersectionObserver.
 */
export function Reveal({
  as: Tag = 'div',
  className = '',
  anim = 'up',
  delay = 0,
  style,
  children,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (!ref.current || seen) return;
    const node = ref.current;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    io.observe(node);
    return () => io.disconnect();
  }, [seen]);

  const mergedStyle: CSSProperties | undefined = delay
    ? { transitionDelay: `${delay}ms`, ...(style ?? {}) }
    : style;

  // Cast through unknown for the polymorphic ref + extra attrs.
  const Component = Tag as unknown as React.ComponentType<Record<string, unknown>>;

  return (
    <Component
      ref={ref as unknown as React.Ref<HTMLElement>}
      data-anim={anim}
      style={mergedStyle}
      className={`reveal ${seen ? 'in' : ''} ${className}`}
      {...rest}
    >
      {children}
    </Component>
  );
}
